import { match } from 'ts-pattern';
import { INSTAGRAM_CONTENT_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import {
  ASSET_TYPE,
  ISearchContentResult,
  YOUTUBE_ASSET_SIZE,
} from '~/searchApi';
import {
  IApiSearchInstagramContent,
  IApiSearchTiktokContent,
  IApiSearchYoutubeContent,
} from '~/searchApi/content/searchContentApi.model';

export const getContentLink = (content: ISearchContentResult) => {
  return match(content.platform)
    .with(SOCIAL_PLATFORMS.instagram, () => content.shareUrl)
    .with(SOCIAL_PLATFORMS.tiktok, () => content.externalUrl)
    .with(
      SOCIAL_PLATFORMS.youtube,
      () => ` https://www.youtube.com/watch?v=${content.networkId}`,
    )
    .exhaustive();
};

export const getIsStoryExpired = (content: ISearchContentResult) => {
  const { publishedAt, createdTime } = content;
  if (
    content.platform === SOCIAL_PLATFORMS.instagram &&
    content.channel === INSTAGRAM_CONTENT_TYPES.story
  ) {
    const hoursInMilliseconds = 24 * 60 * 60 * 1000;
    const currentDate = new Date();
    const createDate = new Date(publishedAt ?? createdTime);

    return currentDate.getTime() - createDate.getTime() > hoursInMilliseconds;
  }
  return false;
};

export const getContentPreviewUrl = (content: ISearchContentResult) => {
  return match(content.type)
    .with(ASSET_TYPE.video, () => {
      const asset = match(content.platform)
        .with(SOCIAL_PLATFORMS.youtube, () => {
          // For youtube, we need to use the high quality asset
          return (
            content.assets.find(
              ({ name }) => YOUTUBE_ASSET_SIZE.high === name,
            ) || content.assets.at(0)
          );
        })
        .otherwise(() => content.assets.at(0));
      return asset?.previewUrl || asset?.thumbnail || asset?.url;
    })
    .otherwise(() => {
      return (
        content.assets.at(0)?.url ||
        content.assets.at(0)?.previewUrl ||
        content.assets.at(0)?.thumbnail
      );
    });
};

// Helper function to determine which metrics to show based on platform and product type
export const getMetricsToShow = (content: ISearchContentResult) => {
  const { platform } = content;

  // Check if platform is Instagram
  if (platform === SOCIAL_PLATFORMS.instagram) {
    const instagramContent = content as IApiSearchInstagramContent;
    const { productType, views, viewsCount, impressions, reach, engagements } =
      instagramContent;

    // For Instagram FEED or REELS
    if (productType === 'FEED' || productType === 'REELS') {
      // If off-platform, only show likes and comments
      if (content.status === 'off-platform') {
        return {
          showView: false,
          showReach: false,
          showEngagements: false,
          viewValue: undefined,
          reachValue: undefined,
          engagementsValue: undefined,
          showLikes: content.likes != null,
          showComments: content.comments != null,
          likesValue: content.likes,
          commentsValue: content.comments,
        };
      }

      // If on-platform, show views, reach, and engagements
      // Use the same logic as SearchContentTable for consistency
      let viewValue;

      if (productType === 'REELS') {
        // For Reels, use viewsCount OR igReelsAggregatedAllPlaysCount (same as SearchContentTable)
        viewValue = viewsCount || content.igReelsAggregatedAllPlaysCount;
      } else {
        // For Posts/Stories, use viewsCount OR views OR impressions
        viewValue = viewsCount ?? views ?? impressions;
      }

      return {
        showView: viewValue != null,
        showReach: reach != null,
        showEngagements: engagements != null,
        viewValue: viewValue,
        reachValue: reach,
        engagementsValue: engagements,
        showLikes: false,
        showComments: false,
        likesValue: undefined,
        commentsValue: undefined,
      };
    }
    // For Instagram STORY, show views and reach
    if (productType === 'STORY') {
      // For Stories, use viewsCount OR views OR impressions
      const viewValue = viewsCount ?? views ?? impressions;

      return {
        showView: viewValue != null,
        showReach: reach != null,
        showEngagements: false,
        viewValue: viewValue,
        reachValue: reach,
        engagementsValue: undefined,
        // Don't show likes and comments for STORY
        showLikes: false,
        showComments: false,
        likesValue: undefined,
        commentsValue: undefined,
      };
    }
  }

  // For TikTok, show videoViews and engagements
  if (platform === SOCIAL_PLATFORMS.tiktok) {
    const tiktokContent = content as IApiSearchTiktokContent;
    const { videoViews, engagements } = tiktokContent;

    return {
      showView: videoViews != null,
      showReach: false,
      showEngagements: engagements != null,
      viewValue: videoViews,
      reachValue: undefined,
      engagementsValue: engagements,
      showLikes: false,
      showComments: false,
      likesValue: undefined,
      commentsValue: undefined,
    };
  }

  // For YouTube, show only viewCount
  if (platform === SOCIAL_PLATFORMS.youtube) {
    const youtubeContent = content as IApiSearchYoutubeContent;
    const { viewCount } = youtubeContent;

    return {
      showView: viewCount != null,
      showReach: false,
      showEngagements: false,
      viewValue: viewCount,
      reachValue: undefined,
      engagementsValue: undefined,
      showLikes: false,
      showComments: false,
      likesValue: undefined,
      commentsValue: undefined,
    };
  }

  // Default: show all if available
  const { views, engagements } = content;
  return {
    showView: views != null,
    showReach: false,
    showEngagements: engagements != null,
    viewValue: views,
    reachValue: undefined,
    engagementsValue: engagements,
    showLikes: false,
    showComments: false,
    likesValue: undefined,
    commentsValue: undefined,
  };
};
